import axios from 'axios';
import { useEffect, useState } from 'react';
import { isHrTeam } from './roles';

export interface HrUser {
  _id: string;
  name: string;
}

/** A staff user's display name, falling back to their id rather than to nothing. */
export const staffUserName = (u: any): string =>
  u?.personalDetails?.fullName
  || [u?.personalDetails?.firstName, u?.personalDetails?.lastName].filter(Boolean).join(' ')
  || u?._id;

/**
 * The recruiters in a `/api/users` list: role HR and HR Lead, as {_id, name}.
 *
 * The one place that decides who counts as a recruiter. Seven pickers each compared the role
 * to the literal HR by hand, so a new recruiting role would have been assignable in one and missing
 * from six. An HR Lead is in the list because she can take a job herself (owner, 2026-11-03).
 * Making a Super Admin or a Marketing user the recruiter on a candidate is still not a thing.
 */
export const toHrUsers = (list: any[] | null | undefined): HrUser[] =>
  (list ?? []).filter((u) => isHrTeam(u?.role)).map((u) => ({ _id: u._id, name: staffUserName(u) }));

/**
 * The recruiters, as {_id, name}, for an owner or assignee picker and for showing an owner's name
 * instead of their id. `enabled` lets a caller skip the request on a page that will never show the
 * picker.
 */
export function useHrUsers(enabled = true): HrUser[] {
  const [users, setUsers] = useState<HrUser[]>([]);
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    axios
      .get('/api/users?page=0&size=200')
      .then((r) => {
        if (cancelled) return;
        setUsers(toHrUsers(r?.data?.content ?? r?.data ?? []));
      })
      .catch(() => {
        /* names are cosmetic; the id still renders */
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);
  return users;
}
